import memoizeOne from 'memoize-one';
import React, {FC, ReactNode, RefObject, useMemo} from 'react';

import {findScrollParent} from '../../../../../libs/shared/core/src/helpers/browser/domHelpers';
import {getHasNewSynchronousP3} from '../../../../../libs/shared/core/src/store/featureFlags/featureFlagsSelectors';
import {Anchor} from '../../../../../libs/shared/ui/src/components/reposition/repositionHelpers';
import {useAppSelector} from '../../../../helpers/appReduxHelpers';
import {AnalyticsIntroEducationTipCoordinator} from './analyticsIntroEducationTipCoordinator';
import {AnalyticsMoreEducationTipCoordinator} from './analyticsMoreEducationTipCoordinator';
import {ChatTeammateHandoffEducationTipCoordinator} from './chatTeammateHandoffEducationTipCoordinator';
import {FirstMessageEducationTipCoordinator} from './firstMessageEducationTipCoordinator';

/*
 * Props.
 */

interface MessageViewerEducationTipProps {
  messageElementRef: RefObject<HTMLDivElement>;
  conversationId: number;
  messageIndex: MessageViewerEducationTipMessageIndex;
}

/*
 * Types.
 */

type MessageViewerEducationTipMessageIndex = (typeof messageViewerEducationTipMessageIndexes)[number];
type MessageViewerEducationTipRendererDictionary = {
  [key in MessageViewerEducationTipMessageIndex]: (
    anchor: Anchor,
    conversationId: number,
    hasNewSynchronousP3: boolean,
  ) => ReactNode;
};

/*
 * Constants.
 */

/** Message viewer education tips will be anchored to a child element with this class, if it exists.  */
export const messageViewerMessageContainerClassName = 'fa-message-viewer-message-container';

export const messageViewerEducationTipMessageIndexes = [0, 1, 2] as const;

const messageViewerEducationTipRenderers: MessageViewerEducationTipRendererDictionary = {
  // First message in the conversation.
  0: (anchor, conversationId) => (
    <>
      <FirstMessageEducationTipCoordinator anchor={anchor} conversationId={conversationId} />
      <AnalyticsIntroEducationTipCoordinator anchor={anchor} conversationId={conversationId} />
      <AnalyticsMoreEducationTipCoordinator anchor={anchor} conversationId={conversationId} />
    </>
  ),
  // Second message in the conversation.
  1: (anchor, conversationId, hasNewSynchronousP3) =>
    !hasNewSynchronousP3 && (
      <ChatTeammateHandoffEducationTipCoordinator anchor={anchor} conversationId={conversationId} />
    ),
  // Third message in the conversation.
  2: (anchor, conversationId, hasNewSynchronousP3) =>
    hasNewSynchronousP3 && (
      <ChatTeammateHandoffEducationTipCoordinator anchor={anchor} conversationId={conversationId} />
    ),
};

/*
 * Component.
 */

export const MessageViewerEducationTip: FC<MessageViewerEducationTipProps> = (props) => {
  const hasNewSynchronousP3 = useAppSelector(getHasNewSynchronousP3);

  const findScrollParentMemoized = useMemo(() => memoizeOne(findScrollParent), []);
  const computeAnchor = useMemo(
    () =>
      memoizeOne((messageElement: HTMLDivElement, timelineElement: Element | null) => {
        const messageRect = messageElement.getBoundingClientRect();
        const timelineRect = timelineElement?.getBoundingClientRect();

        // If the message is taller than P3, use the entire P3 as the anchor for the tip.
        if (timelineRect && messageRect.height >= timelineRect.height) {
          return new DOMRectReadOnly(
            messageRect.left,
            timelineRect.top,
            messageRect.width,
            timelineRect.height,
          );
        }

        // If there's a child element with a message container class, use that as the anchor.
        const messageContainer = messageElement.querySelector(`.${messageViewerMessageContainerClassName}`);
        if (messageContainer) {
          return messageContainer.getBoundingClientRect();
        }

        // Otherwise, use the message.
        return messageRect;
      }),
    [],
  );

  const {messageElementRef} = props;
  const anchor: Anchor = useMemo(
    () => () => {
      const messageElement = messageElementRef.current;

      // This shouldn't happen.
      if (!messageElement) {
        return new DOMRectReadOnly(0, 0, 0, 0);
      }

      return computeAnchor(messageElement, findScrollParentMemoized(messageElement));
    },
    [messageElementRef, findScrollParentMemoized, computeAnchor],
  );

  const render = messageViewerEducationTipRenderers[props.messageIndex];
  return <>{render(anchor, props.conversationId, hasNewSynchronousP3)}</>;
};
